<template>
  <MinimalLayout :loading="step === 'loading' || step === 'selectingAccount'">
    <ExpiredSentStatus v-if="step === 'tokenExpired'" :message="t('link.expired')" />

    <BankAccountList
      v-else-if="step === 'selectAccount'"
      :title="t('link.selectAccount')"
      :select-button-text="t('link.select')"
      :bank-accounts="bankAccountItems"
      @select="(bankAccountId) => handleBankAccountSelected(bankAccountId, accountId)"
    />

    <SuccessStatus
      v-else-if="step === 'success'"
      :message="t('link.accountSynchronized')"
      :subtitle="t('link.closePage')"
    />

    <ErrorStatus v-else>{{ t("link.errorOccurred") }}</ErrorStatus>
  </MinimalLayout>
</template>

<script lang="ts" setup>
import ErrorStatus from "@/components/ErrorStatus.vue";
import ExpiredSentStatus from "@/components/ExpiredSentStatus.vue";
import MinimalLayout from "@/components/MinimalLayout.vue";
import SuccessStatus from "@/components/SuccessStatus.vue";
import { useCollection } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { call, db } from "@/firebase";
import { useUserStore } from "@/store";
import { tokenAuth } from "@/views/Link/tokenAuth";
import type { Account } from "@aircap/common/account";
import type { GclAccount } from "@aircap/common/gcl";
import { logsFullPath, type Log } from "@aircap/common/log";
import { captureException } from "@sentry/vue";
import { addDoc, collection, doc, getDoc, Timestamp } from "firebase/firestore";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useFirebaseAuth } from "vuefire";
import BankAccountList from "./components/BankAccountList.vue";

const { t } = useI18n();
const auth = useFirebaseAuth();
const route = useRoute();
const user = useUserStore();

const accountId = ref<string | null>(null);
const requisitionId =
  route.query.ref && typeof route.query.ref === "string" ? route.query.ref : undefined;
const step = ref<
  "loading" | "error" | "tokenExpired" | "selectAccount" | "selectingAccount" | "success"
>("loading");

const bankAccountsSource = computed(() =>
  accountId.value ? collection<GclAccount>(db, "accounts", accountId.value, "gclAccounts") : null,
);
const { data: bankAccounts } = useCollection<GclAccount>(bankAccountsSource);
const bankAccountItems = computed(() =>
  bankAccounts.value.map((bankAccount) => ({
    id: bankAccount.metadata.id,
    name: bankAccount.metadata.name,
    iban: bankAccount.metadata.iban,
  })),
);

watch(bankAccounts, (bankAccounts) => {
  if (step.value !== "selectAccount") return;
  if (!bankAccounts || bankAccounts.length !== 1 || !bankAccounts[0]) return;
  console.log("Only one GCL account available, automatically selecting it");
  void handleBankAccountSelected(bankAccounts[0].metadata.id, accountId.value);
});

const handleBankAccountSelected = async (bankAccountId: string, accountId: string | null) => {
  if (!requisitionId) return;
  if (step.value === "selectingAccount" || step.value === "success") return;
  console.log(`Account selected: ${bankAccountId}`);
  try {
    if (!accountId) throw new Error("projectId is null");
    console.log("Setting GCL account...");
    step.value = "selectingAccount";
    await call("setGclAccount")({ bankAccountId, accountId, requisitionId });
    console.log("GCL account set successfully");
    step.value = "success";
  } catch (cause) {
    const error = new Error("Error in GCL Return handleAccountSelected function", { cause });
    console.error(error);
    captureException(error);
    step.value = "error";
  }
};

void (async () => {
  try {
    if (!auth) throw new Error("Auth not initialized");

    // Check errors from query, https://bankaccountdata.zendesk.com/hc/en-gb/articles/15926622146716-How-to-recognise-and-handle-end-user-authentication-errors
    if (route.query.error) {
      console.error(
        `Got error ${typeof route.query.error === "string" ? route.query.error : JSON.stringify(route.query.error)} from query in GCL Return`,
        {
          query: route.query,
        },
      );
      captureException(
        new Error(
          `Got error ${typeof route.query.error === "string" ? route.query.error : JSON.stringify(route.query.error)} from query in GCL Return`,
        ),
        {
          extra: { query: route.query },
        },
      );
      step.value = "error";
      return;
    }

    if (!requisitionId) {
      console.error(`Missing requisitionId (param ref) in query`, {
        query: route.query,
      });
      captureException(new Error(`Missing requisitionId (param ref) in query`), {
        extra: { query: route.query },
      });
      step.value = "error";
      return;
    }
    console.log(`Get requisitionId ${requisitionId} from query`);

    if (route.query.token && typeof route.query.token === "string") {
      const result = await tokenAuth(auth, route.query.token);
      if (!result.success) {
        if (result.tokenExpired) {
          step.value = "tokenExpired";
          return;
        }
        throw result.error;
      }
    }

    await user.waitForLoading();

    if (!user.authenticatedUser) throw new Error("User not authenticated");

    if (user.authenticatedUser.authClaims.kind !== "operator")
      throw new Error("User is not operator");

    // Set accountId to local variable to prevent reactivity issues
    const _accountId = user.authenticatedUser.authClaims.accountId;

    const accountSnap = await getDoc(doc<Account>(db, "accounts", _accountId));
    const account = accountSnap.data();
    if (!account) throw new Error(`Project ${_accountId} not found`);
    console.log(`Project ${_accountId} loaded, type is ${account.accountType}`);

    if (account.accountType !== "gcl") throw new Error(`Project account type is not gcl`);

    console.log(`Fetching GCL accounts`);
    await call("fetchGclAccounts")({ accountId: _accountId, requisitionId });

    try {
      await addDoc(collection<Log>(db, logsFullPath(_accountId)), {
        name: "openBanking.returned",
        createdAt: Timestamp.now(),
      });
    } catch (cause) {
      const error = new Error("Error adding log entry", { cause });
      console.error(error);
      captureException(error);
    }

    if (account.bankAccountId) {
      console.log(
        `Project already has an account selected, directly selecting it: ${account.bankAccountId}`,
      );
      void handleBankAccountSelected(account.bankAccountId, _accountId);
      return;
    }

    console.log(`Showing select account step`);
    step.value = "selectAccount";

    console.log(`Setting projectId: ${_accountId}`);
    accountId.value = _accountId;
  } catch (cause) {
    const error = new Error("Error in Link GCLReturn init function", { cause });
    console.error(error);
    captureException(error);
    step.value = "error";
  }
})();
</script>
